import {h, Fragment} from "preact"
import { useAppState, useCurrencyFormatting } from "@nosto/preact";
import { useEffect, useState } from "preact/hooks";
import { formatShopifyPrice } from "../helpers";
import { API_ENDPOINT } from "../config";

import Product from "./Product";

const EMPTY_HITS = [];

function scheduleIdle(callback) {
  if ("requestIdleCallback" in window) {
    const id = window.requestIdleCallback(callback, { timeout: 1000 });
    return () => window.cancelIdleCallback(id);
  }

  const id = window.setTimeout(callback, 0);
  return () => window.clearTimeout(id);
}

function useDesktopBreakpoint() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const handleChange = (event) => setIsDesktop(event.matches);

    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return isDesktop;
}

export default function Products() {
  const appState = useAppState();
  const { format } = useCurrencyFormatting();
  const productResults = appState.response?.products;
  const hits = productResults?.hits || EMPTY_HITS;
  const { loading, pageType } = appState;
  const isDesktop = useDesktopBreakpoint();

  useEffect(() => {
    if (pageType === "category") {
      document.getElementById("nosto-collection-target")?.classList?.remove("min-h-screen");
    }
  }, [pageType]);

  useEffect(() => {
    if (hits.length < 1) return;

    window.dispatchEvent(new CustomEvent("productCardsRendered"));

    return scheduleIdle(() => {
      if (!window.RivoProfileAlpine) return;
      const rivoAccountWidget = document.getElementById("rivo-account-slideout");
      if (rivoAccountWidget) {
        window.RivoProfileAlpine.$data?.(rivoAccountWidget)?.initializeFavoriteButtons?.();
      }
    });
  }, [hits]);

  useEffect(() => {
    const productElements = document.querySelectorAll(".ns-serp [nosto-id]");
    const queryString = Array.from(productElements)
      .map((element) => `id:${element.getAttribute("nosto-id")}`)
      .join(" OR ");

    const API_TOKEN = "8bfc75a336a41a5d26e545ac0b829eb8";
    const ENDPOINT = "https://tnuckadmin.myshopify.com/api/2025-04/graphql.json";

    const query = `
            query @inContext(language: EN, country: ${Shopify.country}) {
                products(query: "${queryString}", first: 100) {
                    nodes {
                        id
                        variants(first: 1) {
                            nodes {
                                price {
                                    amount
                                    currencyCode
                                }
                                compareAtPrice {
                                    amount
                                    currencyCode
                                }
                            }
                        }
                    }
                }
            }
        `;

    fetch(API_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": API_TOKEN,
      },
      body: JSON.stringify({ query }),
    })
      .then((response) => response.json())
      .then((data) => {
        const products = data.data.products.nodes;
        products.forEach((product) => {
          const id = product.id.split("/").pop();
          if (product?.variants?.nodes[0]?.compareAtPrice == null) {
            const price = parseFloat(product.variants.nodes[0].price.amount);
            document.querySelector(`.ns-serp [nosto-id='${id}'] .nosto-p`).innerHTML = formatShopifyPrice(price);
          } else if (
            product?.variants?.nodes[0]?.compareAtPrice?.amount != product?.variants?.nodes[0]?.price?.amount
          ) {
            const price = parseFloat(product.variants.nodes[0].compareAtPrice.amount);
            const listPrice = parseFloat(product.variants.nodes[0].price.amount);
            document.querySelector(`.ns-serp [nosto-id='${id}'] .text-sale.nosto-p`).innerHTML =
              formatShopifyPrice(listPrice);
            document.querySelector(`.ns-serp [nosto-id='${id}'] .nosto-lp`).innerHTML = formatShopifyPrice(price);
          }
        });
      })
      .catch((error) => {
        console.error("GraphQL Error:", error);
        Nosto.migrateToShopifyMarket({
          productSectionElement: ".ns-serp .nosto-product-card",
          productUrlElement: ".ns-serp .nosto-product-link",
          productHandleAttribute: "data-handle",
          priceElement: ".ns-serp .nosto-p",
          listPriceElement: ".ns-serp .nosto-lp",
        });
      });
  }, [productResults?.hits]);

  const eagerProductCount = isDesktop ? 4 : 2;

  return (
    <div
      className="grid grid-cols-2 gap-1.5 gap-y-[1.375rem] mb-[30px] md:gap-y-[3.25rem] md:grid-cols-4 md:px-10"
      style={loading ? "opacity: 0.3;" : ""}
      aria-live="polite"
    >
      {hits.map((hit, index) => (
        <Product
          key={hit.productId}
          product={hit}
          loading={index < eagerProductCount ? "eager" : "lazy"}
          fetchPriority={index === 0 ? "high" : undefined}
          formatCurrency={format}
          isDesktop={isDesktop}
          pageType={pageType}
        />
      ))}
    </div>
  );
}
